import { useMemo, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDbProducts, useDbProductImagesByProductIds } from "@/hooks/use-products";
import { useLanguage } from "@/context/LanguageContext";
import { productPath } from "@/lib/url-slugs";

import { optimizeImageUrl } from "@/lib/image-utils";
import { scoreProductMatch } from "@/lib/product-search";
import { Search, Loader2 } from "lucide-react";

interface SearchDropdownProps {
  query: string;
  onSelect: () => void;
  visible: boolean;
  onClose: () => void;
  /** Mobile variant renders a wide panel and richer product rows. */
  variant?: "desktop" | "mobile";
  className?: string;
}

const MAX_RESULTS = 5;

export default function SearchDropdown({
  query,
  onSelect,
  visible,
  onClose,
  variant = "desktop",
  className,
}: SearchDropdownProps) {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const trimmed = query.trim().toLowerCase();
  const { lang } = useLanguage();
  const isMobile = variant === "mobile";
  const navigate = useNavigate();

  const { data: products = [], isLoading, isError } = useDbProducts({ cardFieldsOnly: true });

  const matches = useMemo(() => {
    if (trimmed.length < 2) return [];

    const scored = products
      .filter((p) => p.status === "active")
      .map((p) => {
        const score = scoreProductMatch(
          { name: p.name, sku: p.sku, brand: p.brand },
          query,
        );
        return score === null ? null : { product: p, score };
      })
      .filter(Boolean) as { product: typeof products[0]; score: number }[];

    scored.sort((a, b) => a.score - b.score);
    return scored.slice(0, MAX_RESULTS).map((s) => s.product);
  }, [products, trimmed, query]);

  const productIds = useMemo(() => matches.map((p) => p.id), [matches]);
  const { data: images = [] } = useDbProductImagesByProductIds(productIds, {
    enabled: productIds.length > 0,
  });

  // Close on outside click
  useEffect(() => {
    if (!visible) return;
    const handler = (e: MouseEvent) => {
      const el = dropdownRef.current;
      if (!el) return;
      // Ignore hidden instances (e.g. the desktop dropdown rendered inside a
      // `hidden md:flex` container on mobile) — otherwise their outside-click
      // handler closes the visible dropdown before a result link is clicked.
      if (el.getClientRects().length === 0) return;
      const target = e.target as Element | null;
      // Taps inside any search dropdown (e.g. the floating toolbar copy) are not "outside".
      if (target?.closest?.("[data-search-dropdown]")) return;
      if (!el.contains(target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [visible, onClose]);

  if (!visible || trimmed.length < 2) return null;
  // Desktop keeps previous behaviour: nothing rendered without matches.
  if (!isMobile && matches.length === 0) return null;

  const searchHref = `/${lang}/${lang === "lv" ? "meklesana" : "search"}?search=${encodeURIComponent(query.trim())}`;

  return (
    <div
      ref={dropdownRef}
      data-search-dropdown=""
      role="listbox"
      aria-label="Meklēšanas rezultāti"
      className={`bg-background border border-border rounded-xl shadow-lg z-[60] overflow-hidden animate-fade-in ${
        isMobile ? "absolute left-3 right-3 top-full mt-2" : "absolute top-full left-0 right-0 mt-1"
      } ${className ?? ""}`}
      style={{ animationDuration: "150ms" }}
    >
      <div className={isMobile ? "max-h-[62vh] overflow-y-auto overscroll-contain" : ""}>
        {isMobile && isLoading && matches.length === 0 && (
          <div className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-muted-foreground">
            <Loader2 size={16} className="animate-spin" />
            Meklē...
          </div>
        )}

        {isMobile && !isLoading && isError && (
          <div className="px-4 py-6 text-sm text-center text-muted-foreground">
            Meklēšana neizdevās. Mēģiniet vēlreiz.
          </div>
        )}

        {isMobile && !isLoading && !isError && matches.length === 0 && (
          <div className="px-4 py-6 text-sm text-center text-muted-foreground">
            Nav atrasts neviens produkts.
          </div>
        )}

        <ul className="divide-y divide-border">
          {matches.map((p) => {
            const img = images.find((i) => i.product_id === p.id);
            const thumbUrl = img ? optimizeImageUrl(img.url, { width: isMobile ? 160 : 80 }) : "/placeholder.svg";
            const hasDiscount = p.compare_at_price && Number(p.compare_at_price) > Number(p.price);
            const discountPct = hasDiscount
              ? Math.round((1 - Number(p.price) / Number(p.compare_at_price)) * 100)
              : 0;

            return (
              <li key={p.id}>
                <Link
                  to={productPath(lang, p.slug)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(productPath(lang, p.slug));
                    onSelect();
                  }}
                  className={`flex gap-3 hover:bg-muted transition-colors ${
                    isMobile ? "items-start px-3 py-3 min-h-[64px]" : "items-center px-4 py-2.5"
                  }`}
                >
                  <img
                    src={thumbUrl}
                    alt={p.name}
                    className={`object-contain rounded bg-muted flex-shrink-0 ${
                      isMobile ? "w-16 h-16" : "w-12 h-12"
                    }`}
                    loading="lazy"
                  />
                  <div className="flex-1 min-w-0">
                    {isMobile && p.brand && (
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{p.brand}</p>
                    )}
                    <p
                      className={`text-sm font-medium text-foreground ${
                        isMobile ? "line-clamp-2 leading-snug" : "truncate"
                      }`}
                    >
                      {p.name}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-0.5">
                      <span className="text-sm font-semibold text-primary">
                        €{Number(p.price).toFixed(2)}
                      </span>
                      {hasDiscount && (
                        <>
                          <span className="text-xs text-muted-foreground line-through">
                            €{Number(p.compare_at_price).toFixed(2)}
                          </span>
                          {isMobile && discountPct > 0 && (
                            <span className="text-[11px] font-semibold text-accent-foreground bg-accent rounded px-1.5 py-0.5">
                              -{discountPct}%
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      <Link
        to={searchHref}
        // Keep input focus so the mobile keyboard doesn't collapse and shift the tap target.
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          navigate(searchHref);
          onSelect();
        }}
        className={`flex items-center justify-center gap-2 px-4 text-sm font-medium text-primary hover:bg-muted border-t border-border transition-colors ${
          isMobile ? "py-3.5 min-h-[48px]" : "py-2.5"
        }`}
      >
        <Search size={14} />
        Skatīt visus rezultātus
      </Link>
    </div>
  );
}
